import React, { useState, useEffect, useRef } from 'react';
import './Settings.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Settings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uploadingBrand, setUploadingBrand] = useState(null);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  const brandFileRefs = useRef({});
  const catFileRefs = useRef({});

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const base = API_URL.replace('/api', '');
    if (path.startsWith('/uploads/')) return `${base}${path}`;
    return `${base}/api/images/${path}`;
  };

  useEffect(() => {
    fetch(`${API_URL}/site-settings`)
      .then(res => res.json())
      .then(data => { setSettings(data); setLoading(false); })
      .catch(() => setLoading(false));
    fetchBrands();
    fetchCategories();
  }, []);

  const fetchBrands = () => {
    fetch(`${API_URL}/brands`)
      .then(res => res.json())
      .then(data => { if (data.success) setBrands(data.data || []); })
      .catch(console.error);
  };

  const fetchCategories = () => {
    fetch(`${API_URL}/categories/with-images`)
      .then(res => res.json())
      .then(data => { if (data.success) setCategories(data.data || []); })
      .catch(console.error);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const toggleSetting = async (key) => {
    const newVal = settings[key] === 'false' ? 'true' : 'false';
    setSettings(prev => ({ ...prev, [key]: newVal }));
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/site-settings/${key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ value: newVal }),
    }).catch(console.error);
  };

  const saveAll = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    const textKeys = [
      'store_name', 'store_address', 'store_phone', 'store_email', 'store_hours',
      'social_facebook', 'social_instagram', 'social_pinterest', 'social_youtube', 'social_tiktok',
      'newsletter_title', 'newsletter_subtitle',
      'announcement_left', 'announcement_right_1', 'announcement_right_2'
    ];
    try {
      await Promise.all(textKeys.map(key =>
        fetch(`${API_URL}/site-settings/${key}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ value: settings[key] || '' }),
        })
      ));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleBrandImageUpload = async (brandId, file) => {
    if (!file) return;
    setUploadingBrand(brandId);
    const token = localStorage.getItem('token');
    try {
      const formData = new FormData();
      formData.append('image', file);
      const uploadRes = await fetch(`${API_URL}/upload/brands`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const uploadData = await uploadRes.json();
      if (uploadData.success) {
        await fetch(`${API_URL}/brands/${brandId}/image`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ image_url: uploadData.imageUrl }),
        });
        fetchBrands();
      }
    } catch (err) {
      console.error('Brand image upload failed:', err);
    } finally {
      setUploadingBrand(null);
    }
  };

  const handleCategoryImageUpload = async (catId, file) => {
    if (!file) return;
    setUploadingCategory(catId);
    const token = localStorage.getItem('token');
    try {
      const formData = new FormData();
      formData.append('image', file);
      const uploadRes = await fetch(`${API_URL}/upload/categories`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const uploadData = await uploadRes.json();
      if (uploadData.success) {
        await fetch(`${API_URL}/categories/${catId}/image`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ image_url: uploadData.imageUrl }),
        });
        fetchCategories();
      }
    } catch (err) {
      console.error('Category image upload failed:', err);
    } finally {
      setUploadingCategory(null);
    }
  };

  const removeBrandImage = async (brandId) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/brands/${brandId}/image`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ image_url: null }),
      });
      fetchBrands();
    } catch (err) {
      console.error('Failed to remove brand image:', err);
    }
  };

  const removeCategoryImage = async (catId) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/categories/${catId}/image`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ image_url: null }),
      });
      fetchCategories();
    } catch (err) {
      console.error('Failed to remove category image:', err);
    }
  };

  const toggleCategoryHomepage = async (catId, show) => {
    const token = localStorage.getItem('token');
    setCategories(prev => prev.map(c => c.id === catId ? { ...c, show_on_homepage: show } : c));
    try {
      await fetch(`${API_URL}/categories/${catId}/homepage`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ show_on_homepage: show }),
      });
    } catch (err) {
      console.error('Failed to toggle category:', err);
      fetchCategories();
    }
  };

  if (loading) return <div className="settings-loading">Loading settings...</div>;

  const homepageSections = [
    { key: 'show_announcement_bar', label: 'Announcement Bar (top of navbar)' },
    { key: 'show_hero_slider', label: 'Hero Slider' },
    { key: 'show_categories', label: 'Shop by Category' },
    { key: 'show_brands', label: 'Shop by Brand' },
    { key: 'show_collections', label: 'Featured Collections' },
    { key: 'show_testimonials', label: 'Trusted by Professionals (Testimonials)' },
    { key: 'show_newsletter', label: 'Newsletter Signup' },
  ];

  return (
    <div className="settings-page">
      <div className="settings-top">
        <h2>Site Settings</h2>
        <button className={`btn-save-all ${saved ? 'saved' : ''}`} onClick={saveAll} disabled={saving}>
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Changes'}
        </button>
      </div>

      {/* Homepage Section Toggles */}
      <div className="settings-card">
        <h3>Homepage Sections</h3>
        <p className="settings-desc">Toggle visibility of homepage sections.</p>
        {homepageSections.map(s => (
          <div key={s.key} className="setting-row">
            <span>{s.label}</span>
            <button
              className={`toggle-btn ${settings[s.key] !== 'false' ? 'on' : 'off'}`}
              onClick={() => toggleSetting(s.key)}
            >
              {settings[s.key] !== 'false' ? 'Visible' : 'Hidden'}
            </button>
          </div>
        ))}
      </div>

      {/* Brand Images */}
      <div className="settings-card">
        <h3>Brand Images</h3>
        <p className="settings-desc">Upload images for each brand shown in "Shop by Brand" on the homepage.</p>
        <div className="image-grid-settings">
          {brands.filter(b => b.product_count > 0).map(brand => (
            <div key={brand.id} className="image-item-settings">
              <div className="image-preview-settings" onClick={() => brandFileRefs.current[brand.id]?.click()}>
                {brand.image_url ? (
                  <img src={getImageUrl(brand.image_url)} alt={brand.name} />
                ) : brand.sample_image ? (
                  <img src={getImageUrl(brand.sample_image)} alt={brand.name} style={{ opacity: 0.5 }} />
                ) : (
                  <div className="image-placeholder-settings">No Image</div>
                )}
                {uploadingBrand === brand.id && <div className="image-uploading-settings">Uploading...</div>}
              </div>
              <div className="image-item-info">
                <span className="image-item-name">{brand.name}</span>
                <div className="image-item-actions">
                  <button className="btn-upload-sm" onClick={() => brandFileRefs.current[brand.id]?.click()}>
                    {brand.image_url ? 'Change' : 'Upload'}
                  </button>
                  {brand.image_url && (
                    <button className="btn-remove-sm" onClick={() => removeBrandImage(brand.id)}>Remove</button>
                  )}
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                hidden
                ref={el => brandFileRefs.current[brand.id] = el}
                onChange={e => handleBrandImageUpload(brand.id, e.target.files[0])}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Category Images & Visibility */}
      <div className="settings-card">
        <h3>Category Images &amp; Homepage Visibility</h3>
        <p className="settings-desc">Toggle which categories appear in "Shop by Category" on the homepage, upload images, and drag to reorder.</p>
        <div className="category-list-settings">
          {categories.map(cat => (
            <div key={cat.id} className={`category-row-settings ${cat.show_on_homepage ? '' : 'category-row-hidden'}`}>
              <div className="category-row-left">
                <div className="category-thumb-settings" onClick={() => catFileRefs.current[cat.id]?.click()}>
                  {cat.image_url ? (
                    <img src={getImageUrl(cat.image_url)} alt={cat.name} />
                  ) : (
                    <span className="thumb-placeholder">+</span>
                  )}
                  {uploadingCategory === cat.id && <div className="image-uploading-settings">...</div>}
                </div>
                <span className="category-row-name">{cat.name}</span>
              </div>
              <div className="category-row-right">
                <div className="category-row-actions">
                  <button className="btn-upload-sm" onClick={() => catFileRefs.current[cat.id]?.click()}>
                    {cat.image_url ? 'Change' : 'Upload'}
                  </button>
                  {cat.image_url && (
                    <button className="btn-remove-sm" onClick={() => removeCategoryImage(cat.id)}>Remove</button>
                  )}
                </div>
                <button
                  className={`toggle-btn ${cat.show_on_homepage ? 'on' : 'off'}`}
                  onClick={() => toggleCategoryHomepage(cat.id, !cat.show_on_homepage)}
                >
                  {cat.show_on_homepage ? 'Visible' : 'Hidden'}
                </button>
              </div>
              <input
                type="file"
                accept="image/*"
                hidden
                ref={el => catFileRefs.current[cat.id] = el}
                onChange={e => handleCategoryImageUpload(cat.id, e.target.files[0])}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Announcement Bar Text */}
      <div className="settings-card">
        <h3>Announcement Bar</h3>
        <p className="settings-desc">Edit the text shown in the top bar of the navbar.</p>
        <label className="settings-label">
          Left Text
          <input value={settings.announcement_left || ''} onChange={e => updateSetting('announcement_left', e.target.value)} placeholder="Free shipping on orders over $75" />
        </label>
        <label className="settings-label">
          Right Text 1
          <input value={settings.announcement_right_1 || ''} onChange={e => updateSetting('announcement_right_1', e.target.value)} placeholder="Same-day pickup available" />
        </label>
        <label className="settings-label">
          Right Text 2
          <input value={settings.announcement_right_2 || ''} onChange={e => updateSetting('announcement_right_2', e.target.value)} placeholder="Join Rewards Program" />
        </label>
      </div>

      {/* Store Information */}
      <div className="settings-card">
        <h3>Store Information</h3>
        <p className="settings-desc">Contact details shown in the footer.</p>
        <label className="settings-label">
          Store Name
          <input value={settings.store_name || ''} onChange={e => updateSetting('store_name', e.target.value)} />
        </label>
        <label className="settings-label">
          Address
          <input value={settings.store_address || ''} onChange={e => updateSetting('store_address', e.target.value)} />
        </label>
        <label className="settings-label">
          Phone Numbers
          <input value={settings.store_phone || ''} onChange={e => updateSetting('store_phone', e.target.value)} placeholder="972-487-1325 / 845-300-5559" />
        </label>
        <label className="settings-label">
          Email
          <input value={settings.store_email || ''} onChange={e => updateSetting('store_email', e.target.value)} />
        </label>
        <label className="settings-label">
          Business Hours
          <input value={settings.store_hours || ''} onChange={e => updateSetting('store_hours', e.target.value)} />
        </label>
      </div>

      {/* Social Media */}
      <div className="settings-card">
        <h3>Social Media Links</h3>
        <p className="settings-desc">Leave blank to hide a social icon.</p>
        <label className="settings-label">
          Facebook URL
          <input value={settings.social_facebook || ''} onChange={e => updateSetting('social_facebook', e.target.value)} placeholder="https://facebook.com/..." />
        </label>
        <label className="settings-label">
          Instagram URL
          <input value={settings.social_instagram || ''} onChange={e => updateSetting('social_instagram', e.target.value)} placeholder="https://instagram.com/..." />
        </label>
        <label className="settings-label">
          Pinterest URL
          <input value={settings.social_pinterest || ''} onChange={e => updateSetting('social_pinterest', e.target.value)} placeholder="https://pinterest.com/..." />
        </label>
        <label className="settings-label">
          YouTube URL
          <input value={settings.social_youtube || ''} onChange={e => updateSetting('social_youtube', e.target.value)} placeholder="https://youtube.com/..." />
        </label>
        <label className="settings-label">
          TikTok URL
          <input value={settings.social_tiktok || ''} onChange={e => updateSetting('social_tiktok', e.target.value)} placeholder="https://tiktok.com/@..." />
        </label>
      </div>

      {/* Newsletter */}
      <div className="settings-card">
        <h3>Newsletter Section</h3>
        <p className="settings-desc">Customize the newsletter signup area on the homepage.</p>
        <label className="settings-label">
          Title
          <input value={settings.newsletter_title || ''} onChange={e => updateSetting('newsletter_title', e.target.value)} />
        </label>
        <label className="settings-label">
          Subtitle
          <input value={settings.newsletter_subtitle || ''} onChange={e => updateSetting('newsletter_subtitle', e.target.value)} />
        </label>
      </div>
    </div>
  );
};

export default Settings;
